import { useState } from 'react';

import { allowedFileTypes } from '../../../../../../../../backend/src/case/doc/allowed-extensions';
import { TrackingEvents } from '@/analytics/events';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { Label } from '@/components/atoms/label';
import MultiSelect from '@/components/atoms/MultiSelect';
import Select from '@/components/atoms/Select';
import { DateRangeFilter } from '@/components/molecules/DateRangeFilter';
import { DocumentTypeFilter } from '@/components/molecules/DocumentTypeFilter';
import FilterPopup from '@/components/molecules/FilterPopup';
import { FilterSwitch } from '@/components/molecules/FilterSwitch';
import type { DocFilters as DocFiltersType, MyOptionType } from '@/types';

// Transform allowed file types into options for the multi-select
const FileExtensionOptions: MyOptionType[] = allowedFileTypes
  .map((fileType) => {
    const extension = fileType.replace('.', '').toLowerCase();
    return {
      label: extension.toUpperCase(),
      value: extension,
    };
  })
  .sort((a, b) => a.label.localeCompare(b.label));

export const DocumentFiltersPopup = ({
  activeFilters,
  setFilters,
  handleClose,
  allDocumentTypes,
  docThemes,
  goToPage,
}: {
  activeFilters: DocFiltersType;
  setFilters: (v: DocFiltersType) => void;
  handleClose: () => void;
  allDocumentTypes: string[];
  docThemes: string[];
  goToPage?: (value: number) => void;
}) => {
  const [processedFilters, setProcessedFilters] = useState<string | undefined>(
    activeFilters.processed === true ? 'true' : activeFilters.processed === false ? 'false' : undefined,
  );
  const [extensionFilters, setExtensionFilters] = useState<MyOptionType[]>(
    activeFilters.extensions?.map((ext) => ({ label: ext.toUpperCase(), value: ext })) || [],
  );
  const [themeFilters, setThemeFilters] = useState<string[]>(activeFilters.documentThemes ?? []);
  const [startDate, setStartDate] = useState<Date | null>(
    activeFilters.startDate ? new Date(activeFilters.startDate) : null,
  );
  const [endDate, setEndDate] = useState<Date | null>(activeFilters.endDate ? new Date(activeFilters.endDate) : null);
  const [documentTypeFilters, setDocumentTypeFilters] = useState<MyOptionType[]>(
    activeFilters.documentTypes
      ?.map((type) => ({
        label: type === '' ? 'Unassigned' : type,
        value: type,
      }))
      ?.filter((type) => type !== null) || [],
  );
  const [hasKeyFactsFilter, setHasKeyFactsFilter] = useState<boolean | null | undefined>(activeFilters.hasKeyFacts);
  const [hasRelevantFactsFilter, setHasRelevantFactsFilter] = useState<boolean | null | undefined>(
    activeFilters.hasRelevantFacts,
  );

  const handleSubmit = () => {
    const newFilters: DocFiltersType = {
      processed: processedFilters === 'true' ? true : processedFilters === 'false' ? false : undefined,
      extensions:
        extensionFilters.length > 0
          ? extensionFilters.map((ext) => ext?.value).filter((v): v is string => Boolean(v))
          : undefined,
      startDate: startDate?.toISOString() || null,
      endDate: endDate?.toISOString() || null,
      documentTypes:
        documentTypeFilters.length > 0
          ? documentTypeFilters.map((type) => type?.value).filter((v): v is string => Boolean(v))
          : undefined,
      documentThemes: themeFilters,
      hasKeyFacts: hasKeyFactsFilter,
      hasRelevantFacts: hasRelevantFactsFilter,
    };

    trackCustomEvent(TrackingEvents.ApplyFiltersOnDocumentsPage);
    setFilters(newFilters);
    handleClose();
  };
  const handleClear = () => {
    setFilters({});
    setProcessedFilters(undefined);
    setExtensionFilters([]);
    setStartDate(null);
    setEndDate(null);
    setDocumentTypeFilters([]);
    setThemeFilters([]);
    setHasKeyFactsFilter(undefined);
    setHasRelevantFactsFilter(undefined);
    handleClose();
  };

  return (
    <FilterPopup
      title="Filter Documents"
      description="Narrow down your document search"
      onApply={handleSubmit}
      onClear={handleClear}
      goToPage={goToPage}
    >
      {/* Key Facts Filter */}
      <FilterSwitch
        id="keyfacts-switch"
        label="Contains Key Facts"
        checked={hasKeyFactsFilter === true}
        onCheckedChange={(checked) => {
          setHasKeyFactsFilter(checked ? true : undefined);
        }}
      />

      {/* Relevant Facts Filter */}
      <FilterSwitch
        id="relevantfacts-switch"
        label="Contains Relevant Facts"
        checked={hasRelevantFactsFilter === true}
        onCheckedChange={(checked) => {
          setHasRelevantFactsFilter(checked ? true : undefined);
        }}
      />

      <div>
        <Label className="mb-2 block">Document Type</Label>
        <DocumentTypeFilter
          selectedTypes={documentTypeFilters}
          setSelectedTypes={(types) => {
            setDocumentTypeFilters(types);
          }}
          allDocumentTypes={allDocumentTypes}
        />
      </div>

      {/* Processing Status */}
      <div>
        <Label className="mb-2 block">Processing Status</Label>
        <Select
          placeholder="All documents"
          options={[
            { label: 'Processed Only', value: 'true' },
            { label: 'Unprocessed Only', value: 'false' },
          ]}
          selectedOption={
            processedFilters !== undefined
              ? {
                  label: processedFilters === 'true' ? 'Processed Only' : 'Unprocessed Only',
                  value: processedFilters,
                }
              : null
          }
          onChange={(newValue) => {
            setProcessedFilters(newValue?.value);
          }}
          className="w-full"
        />
      </div>

      {/* Themes */}
      <div>
        <Label className="mb-2 block">Themes</Label>
        <MultiSelect
          placeholder="All themes"
          options={docThemes.map((theme) => ({
            label: theme,
            value: theme,
          }))}
          selectedOptions={themeFilters.map((theme) => ({ label: theme, value: theme }))}
          onChange={(newValue) => {
            trackCustomEvent(TrackingEvents.FilterDocByTheme);
            setThemeFilters(newValue.filter((option) => option !== null).map((option) => option.value));
          }}
          className="w-full"
        />
      </div>
      {/* File Extensions */}
      <div>
        <Label className="mb-2 block">File Types</Label>
        <MultiSelect
          options={FileExtensionOptions}
          selectedOptions={extensionFilters}
          onChange={(newValue) => {
            setExtensionFilters([...newValue]);
          }}
          placeholder="All file types"
          className="w-full"
        />
      </div>
      {/* Document Date Range */}
      <div>
        <Label className="mb-2 block">Document Date Range</Label>
        <DateRangeFilter startDate={startDate} endDate={endDate} setStartDate={setStartDate} setEndDate={setEndDate} />
      </div>
    </FilterPopup>
  );
};
